import React from "react";
import { Box, Link, TablePagination, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { C } from "../../../common/theme";
import { formatAge } from "../../../common/utils/format";
import { Column, DataTable, StatusPill } from "../../../ui";
import { OrderGroup, SaleLine, formatDate, formatMoney, trackingUrl } from "../salesData";
import { OrderSortKey, SortDir, sortGroups, statusTone } from "../orderView";

interface OrdersTableProps {
  groups: OrderGroup[];
  reportingCurrency: string;
  showIdentity: boolean;
  sortKey: OrderSortKey;
  sortDir: SortDir;
  selectedId: string | null;
  onOpen: (group: OrderGroup) => void;
}

export const timeLabel = (d: Date) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

export function Tracking({ line }: { line: Pick<SaleLine, "tracking_number" | "delivery_provider"> }) {
  if (!line.tracking_number) return <Box component="span" sx={{ color: C.muted }}>–</Box>;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Link
        href={trackingUrl(line.tracking_number)}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={{ fontSize: 13.5, overflowWrap: "anywhere" }}
      >
        {line.tracking_number.toUpperCase()}
      </Link>
      {line.delivery_provider && (
        <Typography variant="caption" sx={{ color: C.muted, display: "block" }} noWrap>
          {line.delivery_provider}
        </Typography>
      )}
    </Box>
  );
}

export function GroupStatus({ group, inline = false }: { group: OrderGroup; inline?: boolean }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: inline ? "row" : "column",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: inline ? "center" : "flex-end",
      }}
    >
      {group.statuses.map((s) => (
        <StatusPill key={s} tone={statusTone(s)}>
          {s}
        </StatusPill>
      ))}
      {!inline && group.attention.length > 0 && (
        <StatusPill tone="warning" title={group.attention.join(" · ")}>
          Needs attention
        </StatusPill>
      )}
    </Box>
  );
}

/** Orders in the current scope, a page at a time. Rows open the order dialog. */
export default function OrdersTable({ groups, reportingCurrency, showIdentity, sortKey, sortDir, selectedId, onOpen }: OrdersTableProps) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  React.useEffect(() => {
    setPage(0);
  }, [groups, sortKey, sortDir]);

  const sorted = React.useMemo(() => sortGroups(groups, sortKey, sortDir), [groups, sortKey, sortDir]);
  const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
  const safePage = Math.min(page, lastPage);
  const rows = sorted.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  const columns: Column<OrderGroup>[] = [
    {
      key: "order",
      header: "Order",
      card: "title",
      minWidth: 200,
      render: (g) => {
        const first = g.lines[0];
        return (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {first?.name ?? "Unnamed listing"}
              {g.lines.length > 1 && (
                <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}>
                  {" "}
                  +{g.lines.length - 1} more
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.25, fontSize: 12.5, color: C.muted, fontWeight: 500 }}>
              <Box component="span" className="mono">
                {g.order.order_id.slice(0, 8)}
              </Box>
              {g.order.is_manual && (
                <StatusPill tone="outline" title={`Created without payment${g.order.created_by ? ` by ${g.order.created_by}` : ""}`}>
                  Manual
                </StatusPill>
              )}
              {g.attention.length > 0 && (
                <Tooltip title={`Needs attention: ${g.attention.join(" · ")}`}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: C.coralText }} aria-label="Needs attention" />
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      key: "placed",
      header: "Placed",
      card: "subtitle",
      render: (g) => (
        <>
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            {formatDate(g.createdAt)}
          </Box>
          <Box component="span" sx={{ color: C.muted, display: { xs: "inline", md: "block" }, fontSize: 12.5, whiteSpace: "nowrap" }}>
            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
              {" · "}
            </Box>
            {timeLabel(g.createdAt)} · {formatAge(g.createdAt)}
          </Box>
        </>
      ),
    },
    ...(showIdentity
      ? [
          {
            key: "people",
            header: "Buyer → seller",
            card: "subtitle",
            hideBelow: 1000,
            render: (g: OrderGroup) => {
              const sellers = Array.from(new Set(g.lines.map((l) => l.seller).filter(Boolean)));
              return (
                <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {g.order.buyer ?? "–"}
                  {g.order.is_first_order && (
                    <Box component="span" sx={{ color: C.muted }}>
                      {" "}
                      (first order)
                    </Box>
                  )}
                  {sellers.length > 0 && (
                    <Box component="span" sx={{ color: C.muted }}>
                      {" → "}
                      {sellers.join(", ")}
                    </Box>
                  )}
                </Box>
              );
            },
          } as Column<OrderGroup>,
        ]
      : []),
    {
      key: "shipsTo",
      header: "Ships to",
      card: "hidden",
      hideBelow: 1200,
      render: (g) => g.order.buyer_country ?? "–",
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      card: "aside",
      render: (g) => (
        <Box sx={{ textAlign: "right" }}>
          <Box sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{formatMoney(g.net, g.order.currency)}</Box>
          {g.order.currency !== reportingCurrency && (
            <Box sx={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>≈ {formatMoney(g.netReporting, reportingCurrency)}</Box>
          )}
        </Box>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "right",
      card: "aside",
      render: (g) => <GroupStatus group={g} />,
    },
    {
      key: "tracking",
      header: "Tracking",
      card: "hidden",
      hideBelow: 1320,
      render: (g) => {
        const trackings = Array.from(new Map(g.lines.filter((l) => l.tracking_number).map((l) => [l.tracking_number, l])).values());
        return trackings.length === 0 ? (
          <Box component="span" sx={{ color: C.muted }}>
            –
          </Box>
        ) : (
          trackings.map((l) => <Tracking key={l.order_item_id} line={l} />)
        );
      },
    },
  ];

  return (
    <DataTable
      aria-label="Orders"
      rows={rows}
      columns={columns}
      getRowId={(g) => g.order.order_id}
      onRowClick={onOpen}
      selectedId={selectedId}
      rowTone={(g) => (g.attention.length > 0 ? "warning" : undefined)}
      empty="No orders match these filters."
      footer={
        sorted.length > 25 ? (
          <TablePagination
            component="div"
            count={sorted.length}
            page={safePage}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Per page"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
            sx={{
              border: 0,
              "& .MuiTablePagination-toolbar": { flexWrap: "wrap", pl: 1, minHeight: 52 },
              "& .MuiTablePagination-actions button": { width: 44, height: 44 },
            }}
          />
        ) : undefined
      }
    />
  );
}
